// v3/app/router/routes.tsx — All v3 route definitions
// All routes under /v3 prefix.
// Uses React.lazy for code splitting on page components.
// Zero imports from v1/v2.

import React, { Suspense } from 'react';
import { lazyWithRetry } from './lazy-with-retry';
import { Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { FeatureRoute } from './FeatureRoute';
import { AppLayout } from '../../layouts/AppLayout';
import { AuthLayout } from '../../layouts/AuthLayout';
import { Skeleton } from '../../design-system/primitives/skeleton';

// ─── Lazy-loaded page components ─────────────────────────────────────────────

const LoginPage = lazyWithRetry(
  () => import('../../features/auth/LoginPage'),
  'LoginPage',
);
const DashboardPage = lazyWithRetry(
  () => import('../../features/dashboard/DashboardPage'),
  'DashboardPage',
);
const LeadsListPage = lazyWithRetry(
  () => import('../../features/leads/LeadsListPage'),
  'LeadsListPage',
);
const LeadCreatePage = lazyWithRetry(
  () => import('../../features/leads/LeadCreatePage'),
  'LeadCreatePage',
);
const LeadDetailPage = lazyWithRetry(
  () => import('../../features/leads/LeadDetailPage'),
  'LeadDetailPage',
);
const ApplicationsListPage = lazyWithRetry(
  () => import('../../features/applications/ApplicationsListPage'),
  'ApplicationsListPage',
);
const ApplicationCreatePage = lazyWithRetry(
  () => import('../../features/applications/ApplicationCreatePage'),
  'ApplicationCreatePage',
);
const ApplicationDetailPage = lazyWithRetry(
  () => import('../../features/applications/ApplicationDetailPage'),
  'ApplicationDetailPage',
);
const RVFormPage = lazyWithRetry(
  () => import('../../features/residence-verification/RVFormPage'),
  'RVFormPage',
);
const RVViewPage = lazyWithRetry(
  () => import('../../features/residence-verification/RVViewPage'),
  'RVViewPage',
);
const GroupsListPage = lazyWithRetry(
  () => import('../../features/groups/GroupsListPage'),
  'GroupsListPage',
);
const GroupCreatePage = lazyWithRetry(
  () => import('../../features/groups/GroupCreatePage'),
  'GroupCreatePage',
);
const GroupDetailPage = lazyWithRetry(
  () => import('../../features/groups/GroupDetailPage'),
  'GroupDetailPage',
);
const TasksListPage = lazyWithRetry(
  () => import('../../features/tasks/TasksListPage'),
  'TasksListPage',
);
const TaskCreatePage = lazyWithRetry(
  () => import('../../features/tasks/TaskCreatePage'),
  'TaskCreatePage',
);
const TaskDetailPage = lazyWithRetry(
  () => import('../../features/tasks/TaskDetailPage'),
  'TaskDetailPage',
);
const LoginHistoryPage = lazyWithRetry(
  () => import('../../features/login-history/LoginHistoryPage'),
  'LoginHistoryPage',
);
const SettingsPage = lazyWithRetry(
  () => import('../../features/settings/SettingsPage'),
  'SettingsPage',
);
const EmiCalculatorPage = lazyWithRetry(
  () => import('../../features/emi-calculator/EmiCalculatorPage'),
  'EmiCalculatorPage',
);

// ─── Loading fallback ────────────────────────────────────────────────────────

function PageLoader() {
  return (
    <div className="p-6 space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

// ─── Route definitions ───────────────────────────────────────────────────────

export function V3Routes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public: Login */}
        <Route element={<AuthLayout />}>
          <Route path="login" element={<LoginPage />} />
        </Route>

        {/* Protected routes — require authentication */}
        <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
            {/* Dashboard — accessible to all authenticated users */}
            <Route element={<FeatureRoute featureId="dashboard" />}>
              <Route index element={<DashboardPage />} />
            </Route>

            {/* Leads */}
            <Route element={<FeatureRoute featureId="leads" />}>
              <Route path="leads" element={<LeadsListPage />} />
              <Route path="leads/create" element={<LeadCreatePage />} />
              <Route path="leads/:phoneNumber" element={<LeadDetailPage />} />
            </Route>

            {/* Applications */}
            <Route element={<FeatureRoute featureId="applications" />}>
              <Route path="applications" element={<ApplicationsListPage />} />
              <Route
                path="applications/create/:leadId"
                element={<ApplicationCreatePage />}
              />
              <Route
                path="applications/:applicationId"
                element={<ApplicationDetailPage />}
              />
            </Route>

            {/* Residence Verification (nested under applications) */}
            <Route element={<FeatureRoute featureId="residence-verification" />}>
              <Route
                path="applications/:applicationId/residence-verification"
                element={<RVFormPage />}
              />
              <Route
                path="applications/:applicationId/residence-verification/view"
                element={<RVViewPage />}
              />
            </Route>

            {/* Groups */}
            <Route element={<FeatureRoute featureId="groups" />}>
              <Route path="groups" element={<GroupsListPage />} />
              <Route path="groups/create" element={<GroupCreatePage />} />
              <Route path="groups/:id" element={<GroupDetailPage />} />
            </Route>

            {/* Tasks */}
            <Route element={<FeatureRoute featureId="tasks" />}>
              <Route path="tasks" element={<TasksListPage />} />
              <Route path="tasks/create" element={<TaskCreatePage />} />
              <Route path="tasks/:taskId" element={<TaskDetailPage />} />
            </Route>

            {/* Login History */}
            <Route element={<FeatureRoute featureId="login-history" />}>
              <Route path="login-history" element={<LoginHistoryPage />} />
            </Route>

            {/* Settings */}
            <Route element={<FeatureRoute featureId="settings" />}>
              <Route path="settings" element={<SettingsPage />} />
            </Route>

            {/* EMI Calculator */}
            <Route element={<FeatureRoute featureId="emi-calculator" />}>
              <Route path="emi-calculator" element={<EmiCalculatorPage />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
